// hubspot.js — MODULES 4+5: HubSpot Form Listener + UTM Injection
//
// MODULE 4 — HubSpot Form Submission Listener
// Listens for V3 (postMessage hsFormCallback) and V4
// (hs-form-event:on-submission:success) form submissions.
// Email goes into PostHog $set only — never as a top-level property.
//
// MODULE 5 — UTM → HubSpot Field Injection
// 1. On load: persist UTM params from the URL into sessionStorage
//    under the pb_ prefix.
// 2. MutationObserver watches for new HubSpot forms and injects the
//    stored UTMs into empty fields.  Disconnects after 15 s.
//
// V2 forms: monkey-patch hbspt.forms.create → onFormReady hook.
// V4 forms: HubSpotFormsV4.getForms() → formInstance.setFieldValue.
//
// Bails early if sessionStorage contains no UTM values.

import { cfg, LOG, dbg } from './config.js';

if (cfg.hubspot_forms) {
  try {

    function onHsFormSubmit(formId, conversionId, email) {
      try {
        if (window.posthog) {
          window.posthog.capture('hs_form_submitted', {
            hs_form_id:      formId,
            hs_conversion_id: conversionId,
            url:             window.location.href,
            '$set': {
              email:            email,
              hs_last_form_id:  formId
            }
          });
        }
      } catch (e) { dbg('posthog capture error', e); }

      try {
        if (window.Reo && email) { window.Reo.identify({ email: email }); }
      } catch (e) { dbg('Reo identify error', e); }
    }

    // V3 forms — postMessage channel
    window.addEventListener('message', function(event) {
      try {
        if (!event.data || event.data.type !== 'hsFormCallback') { return; }
        if (event.data.eventName !== 'onFormSubmitted') { return; }

        var d      = event.data;
        var formId = d.id;
        var convId = d.data && d.data.conversionId;
        var vals   = (d.data && d.data.submissionValues) || {};
        var email  = vals.email || '';

        onHsFormSubmit(formId, convId, email);
        dbg('V3 form submitted', formId);
      } catch (e) { dbg('V3 message listener error', e); }
    });

    // V4 forms — custom DOM event
    window.addEventListener('hs-form-event:on-submission:success', function(event) {
      try {
        // HubSpot V4 exposes either HubSpotFormsV4 or HubspotFormsV4
        var HSObj = window.HubSpotFormsV4 || window.HubspotFormsV4;
        if (!HSObj || !HSObj.getFormFromEvent) { return; }

        var hsform = HSObj.getFormFromEvent(event);
        if (!hsform) { return; }

        var formId = hsform.getFormId ? hsform.getFormId() : '';
        var convId = hsform.getConversionId ? hsform.getConversionId() : '';

        hsform.getFormFieldValues().then(function(fieldValues) {
          try {
            var email = '';
            for (var i = 0; i < fieldValues.length; i++) {
              var item = fieldValues[i];
              // Field name can be "email" or "0-1/email"
              var key = item.name.indexOf('/') > -1
                ? item.name.split('/')[1]
                : item.name;
              if (key === 'email') { email = item.value; break; }
            }
            onHsFormSubmit(formId, convId, email);
            dbg('V4 form submitted', formId);
          } catch (e) { dbg('V4 form field values error', e); }
        }).catch(function(e) { dbg('V4 form promise error', e); });
      } catch (e) { dbg('V4 form event error', e); }
    });

    dbg('HubSpot form listener ready');
  } catch (e) {
    console.error(LOG, 'HubSpot form listener error', e);
  }
}

if (cfg.hubspot_forms) {
  try {
    var UTM_KEYS   = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    var UTM_PREFIX = 'pb_';

    // --- Step 1: persist UTMs from URL ---
    (function storeUtms() {
      try {
        var qs = window.location.search.substring(1);
        var pairs = qs ? qs.split('&') : [];
        for (var p = 0; p < pairs.length; p++) {
          var kv = pairs[p].split('=');
          var key = decodeURIComponent(kv[0]);
          var val = kv.length > 1 ? decodeURIComponent(kv[1]) : '';
          if (UTM_KEYS.indexOf(key) !== -1 && val) {
            sessionStorage.setItem(UTM_PREFIX + key, val);
          }
        }
      } catch (e) { dbg('storeUtms error', e); }
    }());

    // --- Step 2: inject into forms ---
    function getStoredUtms() {
      var data = {};
      for (var k = 0; k < UTM_KEYS.length; k++) {
        var v = sessionStorage.getItem(UTM_PREFIX + UTM_KEYS[k]);
        if (v) { data[UTM_KEYS[k]] = v; }
      }
      return data;
    }

    var stored = getStoredUtms();
    var storedKeys = Object.keys(stored);

    // Bail early — no UTMs means nothing to inject
    if (storedKeys.length > 0) {
      var v2Patched    = false;
      var patchedV4Ids = [];

      // V2: inject into a jQuery-wrapped form element
      function injectV2(jqForm, utms) {
        for (var key in utms) {
          try {
            var field = jqForm.find('input[name="' + key + '"]');
            if (field.length && !field.val()) {
              field.val(utms[key]).change();
              if (field[0]) {
                var ev = document.createEvent('Event');
                ev.initEvent('input', true, true);
                field[0].dispatchEvent(ev);
                var ev2 = document.createEvent('Event');
                ev2.initEvent('change', true, true);
                field[0].dispatchEvent(ev2);
              }
            }
          } catch (e) { dbg('injectV2 field error', e); }
        }
      }

      function patchV2() {
        if (v2Patched) { return; }
        if (!window.hbspt || !window.hbspt.forms) { return; }

        var origCreate = window.hbspt.forms.create;
        window.hbspt.forms.create = function(options) {
          var origReady = options.onFormReady;
          options.onFormReady = function($form) {
            try { injectV2($form, getStoredUtms()); } catch (e) { dbg('onFormReady UTM inject error', e); }
            if (typeof origReady === 'function') { origReady($form); }
          };
          return origCreate.call(window.hbspt.forms, options);
        };
        v2Patched = true;
        dbg('UTM: V2 hbspt.forms.create patched');
      }

      // V4: inject via the HubSpotFormsV4 API
      function checkV4Forms() {
        var HSObj = window.HubSpotFormsV4 || window.HubspotFormsV4;
        if (!HSObj || !HSObj.getForms) { return; }

        var forms = HSObj.getForms();
        if (!forms || !forms.length) { return; }

        var utms = getStoredUtms();

        for (var fi = 0; fi < forms.length; fi++) {
          var formInstance = forms[fi];
          var instanceId   = formInstance.getInstanceId ? formInstance.getInstanceId() : null;
          if (!instanceId || patchedV4Ids.indexOf(instanceId) !== -1) { continue; }

          for (var key in utms) {
            if (!formInstance.setFieldValue) { continue; }

            // Try to find the field's real namespaced name from the DOM
            var realName   = '0-1/' + key; // fallback to HubSpot's default namespace
            var shouldInject = true;
            var inputs = document.querySelectorAll('input[name$="' + key + '"]');
            for (var ii = 0; ii < inputs.length; ii++) {
              if (inputs[ii].id && inputs[ii].id.indexOf(instanceId) > -1) {
                realName = inputs[ii].name;
                shouldInject = !inputs[ii].value; // skip if field already has value
                break;
              }
            }

            if (shouldInject) {
              try { formInstance.setFieldValue(realName, utms[key]); } catch (e) { dbg('setFieldValue error', e); }
            }
          }

          patchedV4Ids.push(instanceId);
        }
      }

      // MutationObserver triggers both V2 patch attempt and V4 form check
      var utmObserver = new MutationObserver(function() {
        patchV2();
        checkV4Forms();
      });

      var observeTarget = document.body || document.documentElement;
      utmObserver.observe(observeTarget, { childList: true, subtree: true });

      // Run immediately for forms already in the DOM
      patchV2();
      checkV4Forms();

      // Auto-disconnect after 15 s — forms should have loaded by then
      setTimeout(function() {
        utmObserver.disconnect();
        dbg('UTM observer disconnected');
      }, 15000);

      dbg('UTM injection ready');
    }
  } catch (e) {
    console.error(LOG, 'UTM injection error', e);
  }
}
